import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom'
import {api} from './Api.js'
import {AudioRecorder} from 'react-audio-voice-recorder';
import styles from './css/Vocabulary.module.css'
import WordModalWindow from "./WordModalWindow";
import VocabularyStats from "./VocabularyStats";
import PreflightLoader, {parseISODuration} from "./PreflightLoader";
import {config} from "./Constants";
import {answersMatch, sentenceContainsForm} from './vocabularyAnswer';

const exerciseLabels = {
    CHOOSING: 'Choose the word',
    SPEECH: 'Say the missing word',
    WRITING: 'Write the word',
    LISTENING: 'Listen and type',
    SENTENCE: 'Translate the sentence'
};

const PartOfSpeech = ({value}) => value ? (
    <span className={styles['part-of-speech']}>
        <i className="material-icons tiny" aria-hidden="true">sell</i>
        <span>{value}</span>
    </span>
) : null;

const Vocabulary = ({showNotification}) => {
    const [showWordModal, setShowWordModal] = useState(false);
    const [wordModalData, setWordModalData] = useState(null);
    const [round, setRound] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answeredCorrectly, setAnsweredCorrectly] = useState(null);
    const [results, setResults] = useState([]);
    const [answer, setAnswer] = useState(null);
    const [gettingSpeechResult, setGettingSpeechResult] = useState(false);
    const [waiting, setWaiting] = useState(false);
    const [speechResult, setSpeechResult] = useState(null);
    const [submiting, setSubmiting] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [comment, setComment] = useState(null);
    const [checking, setChecking] = useState(null);
    const [expectedWaitTime, setExpectedWaitTime] = useState(0);


    const navigate = useNavigate()

    const reset = useCallback(() => {
        setRound(null);
        setCurrentIndex(0);
        setAnsweredCorrectly(null);
        setResults([]);
        setAnswer(null);
        setGettingSpeechResult(false);
        setWaiting(false);
        setSpeechResult(null);
        setSubmiting(null);
        setStatistics(null);
        setComment(null);
        setChecking(null);
    }, []);

    const fetchRound = useCallback(async () => {
        if (config.enablePreflight) {
            const waitRes = await api.fetchWaitTime('/vocabulary/round', navigate, showNotification);
            if (api.isValidResponse(waitRes)) {
                setExpectedWaitTime(parseISODuration(waitRes.data.expectedWaitTime));
            }
        }
        const response = await api.fetchRound(navigate, showNotification);
        if (api.isValidResponse(response)) {
            setRound(response.data.words);
            setWaiting(false);
            setExpectedWaitTime(0);
        }
    }, [navigate, showNotification]);

    const renderInteractiveText = useCallback((text) => {
        const stopWords = new Set([
            "the", "and", "but", "for", "are", "was", "were", "been", "being", "this", "that", "these", "those"
        ]);

        return text
            .split(/(\s+|[.,!?;:]|\b___+\b)/)
            .map((segment, index) => {
                const isWord = /^[a-zA-Zа-яА-ЯёЁčćžšđČĆŽŠĐ]{3,}$/.test(segment);

                const isStopWord = stopWords.has(segment.toLowerCase());

                if (isWord && !isStopWord) {
                    return (
                        <span
                            key={index}
                            style={{cursor: "pointer", borderBottom: "1px dashed #9e9e9e"}}
                            onClick={() => {
                                setWordModalData({text, wordEn: segment});
                                setShowWordModal(true);
                            }}
                        >
            {segment}
          </span>
                    );
                }

                return <span key={index}>{segment}</span>;
            });
    }, []);

    const saveResult = useCallback((success) => {
        setAnsweredCorrectly(success);
        setResults(prevResults => [
            ...prevResults,
            {id: round[currentIndex].id, success, type: round[currentIndex].type}
        ]);
    }, [currentIndex, round]);

    const handleAnswerInner = useCallback((answer, expected) => {
        setAnswer(answer);
        saveResult(answersMatch(answer, expected));
    }, [saveResult]);

    const handleAnswer = useCallback((answer, anyForm) => {
        let answerInner = answer;
        if (anyForm) {
            if (round[currentIndex].forms.some(form => answer.replace(/\W/g, '').toLowerCase() === form.replace(/\W/g, '').toLowerCase())) {
                answerInner = round[currentIndex].value;
            }
        }
        handleAnswerInner(answerInner, round[currentIndex].value);
    }, [currentIndex, handleAnswerInner, round]);

    const handleSentenceAnswer = useCallback((answer) => {
        const example = round[currentIndex].example;
        setAnswer(answer);

        if (answersMatch(answer, example.original)) {
            handleAnswerInner(answer, example.original);
        } else if (sentenceContainsForm(answer, example.form)) {
            setChecking(true);
        } else {
            saveResult(false);
        }
    }, [currentIndex, handleAnswerInner, round, saveResult]);

    const handleIncorrectAnswer = useCallback(() => {
        saveResult(false);
    }, [saveResult]);

    const handleRight = useCallback(() => {
        const indexToUpdate = results.findIndex(value => value.id === round[currentIndex].id);
        let updatedResults = [...results];
        updatedResults[indexToUpdate] = {...updatedResults[indexToUpdate], success: true};
        setResults(updatedResults);
        setAnsweredCorrectly(true);
    }, [currentIndex, results, round]);

    const handleNext = useCallback(() => {
        let innerIndex = currentIndex + 1;
        if (innerIndex >= (round?.length || 0)) {
            setSubmiting(true);
        }
        setCurrentIndex(innerIndex);
        setAnsweredCorrectly(null);
        setAnswer(null);
        setSpeechResult(null);
        setComment(null);
    }, [currentIndex, round?.length]);

    const handleSubmitResults = useCallback(async () => {
        const response = await api.roundResult({words: results}, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setStatistics(response.data);
            setWaiting(false);
            setSubmiting(false);
        }
    }, [navigate, results, showNotification]);

    const handleAudioResult = useCallback(async (blob) => {
        const audioFile = new File([blob], 'voice.wav', {type: 'audio/wav'});
        const response = await api.speech(audioFile, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setSpeechResult(response.data.trim().toLowerCase());
            handleAnswer(response.data.trim().toLowerCase().includes(round[currentIndex].example.form.trim().toLowerCase()) ? round[currentIndex].value : round[currentIndex].ruValue, false);
            setGettingSpeechResult(false);
        }
    }, [currentIndex, handleAnswer, navigate, round, showNotification]);

    const handleSmartCheck = useCallback(async () => {
        try {
            if (config.enablePreflight) {
                const waitRes = await api.fetchWaitTime('/vocabulary/smart', navigate, showNotification);
                if (api.isValidResponse(waitRes)) {
                    setExpectedWaitTime(parseISODuration(waitRes.data.expectedWaitTime));
                }
            }

            const response = await api.smartCheck(round[currentIndex].example.id, answer, navigate, showNotification);
            if (api.isValidResponse(response)) {
                setComment(response.data.comment);
                saveResult(response.data.rating > 2);
            }
        } finally {
            setWaiting(false);
            setChecking(false);
            setExpectedWaitTime(0);
        }
    }, [answer, currentIndex, navigate, round, saveResult, showNotification]);

    useEffect(() => {
        if (round === null && !waiting) {
            setWaiting(true);
            fetchRound();
        }
    }, [round, waiting, fetchRound]);

    useEffect(() => {
        if (submiting === true) {
            setWaiting(true);
            handleSubmitResults();
        }
    }, [submiting, handleSubmitResults]);

    useEffect(() => {
        if (checking === true) {
            setWaiting(true);
            handleSmartCheck();
        }
    }, [checking, handleSmartCheck]);

    const handleChange = (event) => {
        setAnswer(event.target.value);
    };

    const preloader = () => {
        return (
            <div className="common-preloader-form">
                <PreflightLoader expectedWaitTime={expectedWaitTime}/>
            </div>
        );
    };

    const renderWordForm = () => {
        if (!Array.isArray(round) || round.length === 0) {
            return (
                <div className="center-align">
                    <h5 className="grey-text">No words found for this session</h5>
                    <button className="btn waves-effect waves-light mt20" onClick={() => reset()}>
                        <i className="material-icons left">refresh</i>
                        Retry
                    </button>
                </div>
            )
        }

        if (currentIndex >= round.length && statistics !== null) {
            return (
                <div className={styles['vocabulary-form']}>
                    <div className="center-align">
                        <i className="material-icons large green-text">check_circle</i>
                        <h4 className="common-word mt10">You've completed the round</h4>
                    </div>

                    <VocabularyStats vocabulary={statistics}/>

                    <div className={styles['result-buttons']} style={{marginTop: '30px', justifyContent: 'center'}}>
                        <button className="waves-effect waves-light btn-large blue common-button"
                                onClick={() => reset()}>
                            <i className="material-icons left">refresh</i>
                            Next Round
                        </button>
                        <button className="waves-effect waves-light btn-large grey common-button"
                                onClick={() => navigate('/')}>
                            <i className="material-icons left">home</i>
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            )
        }

        const word = round[currentIndex];
        if (!word) return null;

        const progress = (currentIndex / round.length) * 100;
        const exampleTranslation = typeof word.example?.translation === 'string'
            ? word.example.translation.trim()
            : '';
        const shouldShowExampleTranslation =
            (word.type === 'SPEECH' || word.type === 'LISTENING') && Boolean(exampleTranslation);

        return (
            <div className={styles['vocabulary-form']}>
                <div className={styles['exercise-header']}>
                    <div>
                        <span className={styles['exercise-kicker']}>Vocabulary practice</span>
                        <strong>{currentIndex + 1} of {round.length}</strong>
                    </div>
                    <span className={styles['exercise-type']}>
                        <i className="material-icons tiny" aria-hidden="true">auto_awesome</i>
                        {exerciseLabels[word.type] || 'Practice'}
                    </span>
                </div>
                <div className={`progress grey lighten-4 ${styles['progress-track']}`}>
                    <div className="determinate blue"
                         style={{width: `${progress}%`}}></div>
                </div>

                {word.type === 'CHOOSING' && (
                    <div className="center-align">
                        <div className="common-word"
                             style={{fontSize: '2rem', marginBottom: '10px'}}>{word.ruValue}</div>
                        <PartOfSpeech value={word.partOfSpeech}/>

                        <div className={styles['examples-list']}>
                            {word.examples.map((example, i) => (
                                <div key={i} className={styles['example-card']}>
                                    <i className="material-icons" aria-hidden="true">format_quote</i>
                                    <div>
                                        {renderInteractiveText(example)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {answeredCorrectly === null ? (
                            <div className={`row ${styles['options-grid']}`}>
                                {word.options.map(option => (
                                    <div className="col s12 m6" key={option}>
                                        <button
                                            className={`waves-effect waves-light btn-large white blue-text text-darken-2 ${styles['option-button']}`}
                                            onClick={() => handleAnswer(option, false)}>
                                            {option}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={`center-align ${styles['selected-answer']}`}>
                                <button className={`btn-large disabled ${styles['option-button']}`}>
                                    {answer}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {word.type === 'SPEECH' && (
                    <div className="center-align">
                        <div className="card-panel grey lighten-4 italic"
                             style={{fontSize: '1.2rem', boxShadow: 'none', borderLeft: '4px solid #2196f3'}}>
                            {renderInteractiveText(word.definition)}
                        </div>
                        <PartOfSpeech value={word.partOfSpeech}/>
                        <div className="common-word"
                             style={{fontSize: '1.6rem', marginBottom: word.example?.formDescription ? '5px' : '20px'}}>
                            {renderInteractiveText(word.example.masked)}
                        </div>
                        {word.example?.formDescription && (
                            <div className="blue-text text-lighten-3 italic"
                                 style={{fontSize: '0.9rem', marginBottom: '20px'}}>
                                <i className="material-icons tiny"
                                   style={{verticalAlign: 'middle', marginRight: '5px'}}>info_outline</i>
                                <span style={{verticalAlign: 'middle'}}>{word.example.formDescription}</span>
                            </div>
                        )}

                        {gettingSpeechResult && preloader()}

                        {answeredCorrectly === null && !gettingSpeechResult && (
                            <div style={{
                                padding: '20px 0',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                                <AudioRecorder
                                    onRecordingComplete={blob => {
                                        setGettingSpeechResult(true);
                                        handleAudioResult(blob);
                                    }}
                                    onNotAllowedOrFound={() => showNotification("Check your microphone")}
                                    audioTrackConstraints={{noiseSuppression: true, echoCancellation: true}}
                                    showVisualizer={true}
                                />
                                <p className="grey-text mt10">Click to record your answer</p>
                            </div>
                        )}

                        {answeredCorrectly !== null && !answeredCorrectly && word.example.speech && (
                            <div className="card-panel blue lighten-5" style={{padding: '10px', boxShadow: 'none'}}>
                                <audio controls style={{width: '100%', height: '40px'}}>
                                    <source src={`data:audio/mpeg;base64,${word.example.speech}`} type="audio/mpeg"/>
                                </audio>
                            </div>
                        )}
                    </div>
                )}

                {word.type === 'WRITING' && (
                    <div className="center-align">
                        <div className="common-word"
                             style={{fontSize: '2.2rem', marginBottom: '10px'}}>{word.ruValue}</div>
                        <PartOfSpeech value={word.partOfSpeech}/>

                        <div className={styles['examples-list']}>
                            {word.examples.map((example, i) => (
                                <div key={i} className={styles['example-card']}>
                                    <i className="material-icons" aria-hidden="true">format_quote</i>
                                    <div>
                                        {renderInteractiveText(example)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className={`input-field ${styles['answer-field']}`}>
                            <input
                                id="writing_answer"
                                key={currentIndex}
                                type="text"
                                className={`center-align ${styles['answer-input']}`}
                                disabled={answeredCorrectly !== null}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && answer && answer.trim().length > 0 && answeredCorrectly === null) {
                                        handleAnswer(answer, true)
                                    }
                                }}
                                onChange={handleChange}
                                value={answer || ''}
                                placeholder="Type the English word"
                                autoFocus
                            />
                            {answeredCorrectly === null && (
                                <button
                                    className={`btn-large waves-effect waves-light blue ${styles['submit-button']} ${!answer || answer.trim().length === 0 ? 'disabled' : ''}`}
                                    onClick={() => handleAnswer(answer, true)}>
                                    Submit
                                    <i className="material-icons right">send</i>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {word.type === 'LISTENING' && (
                    <div className="center-align">
                        <div className="card-panel blue lighten-5"
                             style={{padding: '15px', marginBottom: '25px', borderRadius: '12px'}}>
                            <audio controls autoPlay style={{width: '100%'}} key={currentIndex}>
                                <source src={`data:audio/mpeg;base64,${word.example.speech}`} type="audio/mpeg"/>
                            </audio>
                        </div>

                        <PartOfSpeech value={word.partOfSpeech}/>

                        <div className="common-word"
                             style={{fontSize: '1.6rem', marginBottom: word.example?.formDescription ? '5px' : '20px'}}>
                            {renderInteractiveText(word.example.masked)}
                        </div>
                        {word.example?.formDescription && (
                            <div className="blue-text text-lighten-3 italic"
                                 style={{fontSize: '0.9rem', marginBottom: '20px'}}>
                                <i className="material-icons tiny"
                                   style={{verticalAlign: 'middle', marginRight: '5px'}}>info_outline</i>
                                <span style={{verticalAlign: 'middle'}}>{word.example.formDescription}</span>
                            </div>
                        )}

                        <div className={`input-field ${styles['answer-field']}`}>
                            <input
                                id="listening_answer"
                                key={currentIndex}
                                type="text"
                                className={`center-align ${styles['answer-input']}`}
                                disabled={answeredCorrectly !== null}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && answer && answer.trim().length > 0 && answeredCorrectly === null) {
                                        handleAnswerInner(answer, word.example.form)
                                    }
                                }}
                                onChange={handleChange}
                                value={answer || ''}
                                placeholder="What did you hear?"
                                autoFocus
                            />
                            {answeredCorrectly === null && (
                                <button
                                    className={`btn-large waves-effect waves-light blue ${styles['submit-button']} ${!answer || answer.trim().length === 0 ? 'disabled' : ''}`}
                                    onClick={() => handleAnswerInner(answer, word.example.form)}>
                                    Submit
                                    <i className="material-icons right">send</i>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {word.type === 'SENTENCE' && (
                    <div className="center-align">
                        <div className={styles['sentence-prompt']}>
                            <div className={styles['sentence-prompt-label']}>Translate this sentence
                            </div>
                            {word.example.translation}
                        </div>

                        <div className={`input-field ${styles['answer-field']}`}>
                            <textarea
                                id="sentence_answer"
                                key={currentIndex}
                                className={`materialize-textarea center-align ${styles['sentence-input']}`}
                                disabled={answeredCorrectly !== null}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && answer && answer.trim().length > 0 && answeredCorrectly === null) {
                                        e.preventDefault();
                                        handleSentenceAnswer(answer);
                                    }
                                }}
                                onChange={handleChange}
                                value={answer || ''}
                                placeholder="Translate to English"
                                autoFocus
                            />
                            {answeredCorrectly === null && (
                                <button
                                    className={`btn-large waves-effect waves-light blue ${styles['submit-button']} ${!answer || answer.trim().length === 0 ? 'disabled' : ''}`}
                                    onClick={() => handleSentenceAnswer(answer)}>
                                    Check translation
                                    <i className="material-icons right">spellcheck</i>
                                </button>
                            )}
                        </div>

                        {comment && (
                            <div className={`card-panel grey lighten-4 left-align ${styles['smart-feedback']}`}>
                                <i className="material-icons left orange-text">info</i>
                                <span>{comment}</span>
                            </div>
                        )}
                    </div>
                )}

                {answeredCorrectly !== null && (
                    <div
                        className={`card-panel animate-fade-in ${styles['answer-feedback']} ${answeredCorrectly ? styles['answer-feedback-correct'] : styles['answer-feedback-incorrect']}`}>
                        <div className={`row valign-wrapper ${styles['feedback-row']}`}>
                            <div className={`col s2 center-align ${styles['feedback-icon']}`}>
                                <i className={`material-icons medium ${answeredCorrectly ? 'green-text' : 'red-text'}`}>
                                    {answeredCorrectly ? 'check_circle' : 'cancel'}
                                </i>
                            </div>
                            <div className={`col s10 ${styles['feedback-content']}`}>
                                <h6 style={{fontWeight: 'bold', margin: '0 0 5px 0'}}>
                                    {answeredCorrectly ? 'Excellent!' : 'Correct Answer:'}
                                </h6>
                                <div className={styles['answer-details']}>
                                    {word.type === 'SENTENCE' ? (
                                        answeredCorrectly ? 'Perfect translation!' :
                                            <b>{renderInteractiveText(word.example.original)}</b>
                                    ) : (
                                        <>
                                            <div className={styles['answer-word-line']}>
                                                <b>{word.type === 'SPEECH' || word.type === 'LISTENING' ? word.example.form : word.value}</b>
                                                <PartOfSpeech value={word.partOfSpeech}/>
                                                {word.transcription && (
                                                    <span className={styles.transcription}>[{word.transcription}]</span>
                                                )}
                                            </div>
                                            <span className={styles['answer-translation']}>{word.ruValue}</span>
                                        </>
                                    )}
                                </div>
                                {!answeredCorrectly && speechResult && (
                                    <div className="mt10 orange-text text-darken-3" style={{fontSize: '0.9rem'}}>
                                        You said: <b>"{speechResult}"</b>
                                    </div>
                                )}
                                {shouldShowExampleTranslation && (
                                    <div className={styles['example-translation']}>
                                        <div className={styles['example-translation-label']}>
                                            <i className="material-icons tiny" aria-hidden="true">translate</i>
                                            <span>Example translation</span>
                                        </div>
                                        <p lang="ru">{exampleTranslation}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles['result-buttons']}>
                    {answeredCorrectly !== null && !answeredCorrectly && (
                        <button className="waves-effect waves-light btn-large grey darken-1" onClick={() => {
                            handleRight();
                            handleNext();
                        }}>
                            <i className="material-icons left">thumb_up</i>
                            I was right
                        </button>
                    )}
                    {answeredCorrectly === null && word.type !== 'CHOOSING' && (
                        <button className="waves-effect waves-light btn-large grey lighten-1"
                                onClick={handleIncorrectAnswer}>
                            <i className="material-icons left">help_outline</i>
                            I don't know
                        </button>
                    )}
                    {answeredCorrectly !== null && !gettingSpeechResult && (
                        <button className="waves-effect waves-light btn-large blue" onClick={handleNext}>
                            Next
                            <i className="material-icons right">arrow_forward</i>
                        </button>
                    )}
                </div>
            </div>
        );
    };

    if (round === null || waiting || submiting) {
        return preloader();
    }

    return (
        <div>{showWordModal && (
            <div style={{
                position: "fixed",
                top: 0, left: 0, width: "100vw", height: "100vh",
                backgroundColor: "rgba(0, 0, 0, 0.7)", zIndex: 1000,
                display: 'flex', justifyContent: 'center', alignItems: 'center'
            }}>
                <WordModalWindow
                    wordEn={wordModalData.wordEn}
                    text={wordModalData.text}
                    onClose={() => setShowWordModal(false)}
                    showNotification={showNotification}
                    navigate={navigate}
                />
            </div>
        )}
            <div className={`container common-container ${styles['page-container']}`}>
                <div className={`card ${styles['vocabulary-card']}`}>
                    <div className="card-content">
                        {renderWordForm()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Vocabulary;
